<div class="space-y-8">

  <!-- Quick actions -->
  <section class="space-y-4">

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">

      <!-- Generate a recipe with the assistant -->
      <button
        type="button"
        class="card border border-base-300 bg-base-100 text-left transition-colors hover:bg-base-200"
        (click)="createRecipeWithAssistant()"
      >
        <div class="card-body flex-row items-center gap-4 p-5">
          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <fa-icon [icon]="faWandMagicSparkles" size="lg"></fa-icon>
          </span>
          <span class="min-w-0">
            <span class="block font-semibold">Rezept generieren</span>
            <span class="block text-sm text-base-content/60">Mit dem Assistenten erstellen</span>
          </span>
        </div>
      </button>

      <!-- Create a new product -->
      <button
        type="button"
        class="card border border-base-300 bg-base-100 text-left transition-colors hover:bg-base-200"
        (click)="createProduct()"
      >
        <div class="card-body flex-row items-center gap-4 p-5">
          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
            <fa-icon [icon]="faBoxOpen" size="lg"></fa-icon>
          </span>
          <span class="min-w-0">
            <span class="block font-semibold">Produkt erstellen</span>
            <span class="block text-sm text-base-content/60">Neues Produkt anlegen</span>
          </span>
        </div>
      </button>

      <!-- Scan mode -->
      <button
        type="button"
        class="card border border-base-300 bg-base-100 text-left transition-colors hover:bg-base-200"
        (click)="openScanMode()"
      >
        <div class="card-body flex-row items-center gap-4 p-5">
          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-info/10 text-info">
            <fa-icon [icon]="faBarcode" size="lg"></fa-icon>
          </span>
          <span class="min-w-0 flex-1">
            <span class="block font-semibold">Scan-Modus</span>
            <span class="block text-sm text-base-content/60">Produkte per Barcode scannen</span>
          </span>
          <fa-icon [icon]="faArrowRight" class="text-base-content/40"></fa-icon>
        </div>
      </button>

      <!-- Second row: quick-add on the left third, open reminders beside it. -->

      <!-- Inline: add to shopping list -->
      <div class="card border border-base-300 bg-base-100">
        <div class="card-body gap-3 p-5">
          <div class="flex items-center gap-4">
            <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <fa-icon [icon]="faCartShopping" size="lg"></fa-icon>
            </span>
            <span class="min-w-0">
              <span class="block font-semibold">Zur Einkaufsliste</span>
              <span class="block text-sm text-base-content/60">Schnell etwas notieren</span>
            </span>
          </div>

          <app-shopping-quick-add [householdId]="household?.id"></app-shopping-quick-add>
        </div>
      </div>

      <!-- Open consumption reminders. Kept out of the grid entirely when there
           is nothing to ask, so the row doesn't leave a gap behind. -->
      @if (hasQuickChecks()) {
        <app-quick-check-teaser class="block min-w-0 sm:col-span-2"></app-quick-check-teaser>
      }

    </div>
  </section>

  <!-- Primary widgets: next meals (~1/3) + "cook now" (~2/3), side by side on wide screens. -->
  <div class="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_2fr] xl:items-start">

    <!-- Upcoming meals widget -->
    <section class="space-y-4 min-w-0">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold tracking-tight">Nächste Mahlzeiten</h2>
        <a class="link link-primary text-sm" [routerLink]="['/meal-plan']">
          Menüplan
        </a>
      </div>

      <ui-card padding="none" [shadow]="false">
        @if (upcomingMeals().length) {
          <ul class="divide-y divide-base-200">
            @for (meal of upcomingMeals(); track meal.entry.id ?? meal.entry.clientId) {
              <li>
                <a
                  class="flex items-center gap-3 px-4 py-3 transition hover:bg-base-200/60"
                  [routerLink]="['/meal-plan']"
                >
                  <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-base-200 text-base-content/70">
                    <fa-icon [icon]="faUtensils"></fa-icon>
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block truncate font-medium">{{ meal.entry.title }}</span>
                    <span class="block text-sm text-base-content/60">
                      {{ dayLabel(meal.dayKey) }} · {{ meal.slotLabel }}@if (meal.entry.servings) { · {{ meal.entry.servings }} Portionen}
                    </span>
                  </span>
                  <fa-icon [icon]="faArrowRight" class="text-base-content/40"></fa-icon>
                </a>
              </li>
            }
          </ul>
        } @else {
          <div class="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <fa-icon [icon]="faCalendarDays" size="lg" class="text-base-content/40"></fa-icon>
            <p class="text-sm text-base-content/60">
              {{ mealsLoading() ? 'Mahlzeiten werden geladen …' : 'Keine geplanten Mahlzeiten in den nächsten Tagen.' }}
            </p>
            @if (!mealsLoading()) {
              <a class="link link-primary text-sm" [routerLink]="['/meal-plan']">
                Menü planen
              </a>
            }
          </div>
        }
      </ui-card>
    </section>

    <!-- "Cook Now" recipe suggestions -->
    <section class="space-y-4 min-w-0">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold tracking-tight">Jetzt kochen</h2>
        <button
          type="button"
          class="btn btn-ghost btn-sm gap-2 text-primary"
          (click)="suggestions.reroll()"
          [disabled]="suggestions.loading()"
          title="Neue Vorschläge"
          aria-label="Neue Vorschläge">
          <fa-icon [icon]="faShuffle"></fa-icon>
          <span class="hidden sm:inline">Mischen</span>
        </button>
      </div>

      <app-recipe-suggestions-teaser
        #suggestions
        [householdId]="household?.id"
        [limit]="6"
        [reloadToken]="reloadToken">
      </app-recipe-suggestions-teaser>
    </section>

  </div>

  <!-- Inventory widgets: expiring (~1/3) + open products (~2/3), side by side on wide screens. -->
  <div class="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_2fr] xl:items-start">

    <!-- Expiring -->
    <section class="space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold tracking-tight">Expiring Soon</h2>
        <a class="link link-primary text-sm" [routerLink]="['/expiring-products']">
          View all
        </a>
      </div>

      <app-expiring-products-teaser
        [householdId]="household?.id"
        [from]="now()" [to]="nextWeek()"
        [limit]="12"
        [reloadToken]="reloadToken">
      </app-expiring-products-teaser>
    </section>

    <!-- Open products -->
    <section class="space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold tracking-tight">Open Products</h2>
        <a
          class="link link-primary text-sm"
          [routerLink]="['/products']"
          [queryParams]="{ filter: 'inStock' }">
          View all
        </a>
      </div>

      <app-open-products-teaser
        class="block"
        [householdId]="household?.id"
        [limit]="12"
        [reloadToken]="reloadToken">
      </app-open-products-teaser>
    </section>

  </div>

</div>
